/**
 * Model for in-chat voice recording state (PRD-941, "I Usually Talk Too Loud").
 *
 * Plain JS + observer pattern, matching the project's MVVM convention. Holds recording
 * state only; the ViewModel owns the browser APIs (getUserMedia / MediaRecorder /
 * AudioContext) and drives these setters.
 *
 * phase:
 *   idle               — nothing happening
 *   requesting         — awaiting mic permission
 *   denied             — user/browser denied mic access
 *   recording          — actively capturing
 *   paused             — capture paused
 *   finalizing         — stopping the recorder, assembling the blob
 *   review             — recording captured, prompting for a name before submit
 *   submitting         — uploading + creating the dataset
 *   submitted          — done
 *   error              — capture or submit failed
 */

export const MAX_DURATION_MS = 10 * 60 * 1000; // hard cap: auto-finalize at 10:00
export const WARN_AT_MS = 9 * 60 * 1000;       // warn the user at 9:00
export const TIMESLICE_MS = 1000;              // MediaRecorder chunk cadence (also draft cadence)
export const MAX_LEVEL_SAMPLES = 60;           // rolling window for the live level chart (~12s @ 200ms)

/** Two-digit-padded default name: "Voice note — 2026-06-15 09:31". */
export function defaultRecordingName(date = new Date()) {
  const pad = (n) => String(n).padStart(2, '0');
  const d = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  const t = `${pad(date.getHours())}:${pad(date.getMinutes())}`;
  return `Voice note — ${d} ${t}`;
}

export class VoiceRecordingModel {
  constructor() {
    this.phase = 'idle';
    this.elapsedMs = 0;
    this.level = 0;          // 0..1 input level for the meter
    this.levelHistory = [];  // rolling 0..1 samples for the live level chart
    this.warned = false;     // 9:00 warning shown
    this.error = null;
    this.draftId = null;     // IndexedDB draft id for this recording
    this.recordedBlob = null;
    this.mimeType = null;
    this.name = '';
    this.isDraftRestored = false; // true when this recording came from a recovered draft
    this.listeners = [];
  }

  getState() {
    return {
      phase: this.phase,
      elapsedMs: this.elapsedMs,
      level: this.level,
      levelHistory: this.levelHistory,
      warned: this.warned,
      error: this.error,
      draftId: this.draftId,
      recordedBlob: this.recordedBlob,
      mimeType: this.mimeType,
      name: this.name,
      isDraftRestored: this.isDraftRestored,
      durationSeconds: Math.round(this.elapsedMs / 1000),
    };
  }

  subscribe(listener) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  notify() {
    const state = this.getState();
    this.listeners.forEach((l) => l(state));
  }

  setPhase(phase) {
    this.phase = phase;
    this.notify();
  }

  setError(error) {
    this.error = error;
    if (error) this.phase = 'error';
    this.notify();
  }

  setElapsed(ms) {
    this.elapsedMs = ms;
    if (!this.warned && ms >= WARN_AT_MS) this.warned = true;
    this.notify();
  }

  setLevel(level) {
    this.level = level;
    // Keep a rolling window of samples (new array ref so consumers re-render cleanly).
    this.levelHistory = [...this.levelHistory, level].slice(-MAX_LEVEL_SAMPLES);
    this.notify();
  }

  setDraftId(draftId) {
    this.draftId = draftId;
    this.notify();
  }

  setName(name) {
    this.name = name;
    this.notify();
  }

  setRecorded(blob, mimeType) {
    this.recordedBlob = blob;
    this.mimeType = mimeType;
    this.notify();
  }

  /** Load a recovered draft into the model so the user can name + submit it. */
  loadDraft({ draftId, blob, mimeType, durationSeconds, name }) {
    this.draftId = draftId;
    this.recordedBlob = blob;
    this.mimeType = mimeType;
    this.elapsedMs = (durationSeconds || 0) * 1000;
    this.name = name || defaultRecordingName();
    this.isDraftRestored = true;
    this.phase = 'review';
    this.notify();
  }

  reset() {
    this.phase = 'idle';
    this.elapsedMs = 0;
    this.level = 0;
    this.levelHistory = [];
    this.warned = false;
    this.error = null;
    this.draftId = null;
    this.recordedBlob = null;
    this.mimeType = null;
    this.name = '';
    this.isDraftRestored = false;
    this.notify();
  }
}
